import "./index.scss";

import {
  Box,
  Checkbox,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ReactComponent as CheckboxOff } from "assets/images/icons/checkbox-off.svg";
import { ReactComponent as CheckboxOn } from "assets/images/icons/checkbox-on.svg";
import ConfirmationBox from "components/ConfirmationBox";
import Dropdown from "components/Dropdown";
import Pagination, { PAGE_OPTIONS } from "components/Pagination";
import ScreenLoader from "components/ScreenLoader";
import SearchBar from "components/SearchBar";
import Tabs from "components/Tabs";
import {
  DEFAULT_MEMBER_STATUS,
  MEMBER_STATUS_ALLOWED_FOR_DELETE,
  MSG_OPERATION_FAILED,
} from "constants/index";
import CustomSelect from "containers/Members/Select";
import SortButton from "containers/Members/SortButton";
import { useQueryParams } from "hooks/useQueryParams";
import { TPagination } from "modules/api/type";
import { getLkpInstructorList } from "modules/lookup/lookupServices";
import {
  getMemberList,
  massDelete,
  massReassignInstructor,
  updateMemberStatus,
} from "modules/member/memberServices";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import MemberFormModal from "../Members/MemberFormModal";
import ActionButtons from "./ActionButtons";
import MassInstructorSwitchModal from "./MassInstructorSwitchModal";
import MassStatusUpdateModal from "./MassStatusUpdateModal";
import { TableItem } from "./TableComponents";

function MemberTasks() {
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchBy, setSearchBy] = useState<string>("LAST_NAME");
  const [params, setParams, defaultParams] = useQueryParams({
    limit: 10,
    my_members_only: true,
    page: 1,
    search: "",
    sort_by: "LAST_NAME",
    sort_order: "ASC",
    status: DEFAULT_MEMBER_STATUS,
    workout_plan_availability: "ALL",
  });
  const [pagination, setPagination] = useState<TPagination>({
    current_page: 0,
    data_from_offset: 0,
    data_to_offset: 0,
    per_page: PAGE_OPTIONS[0],
    total_pages: 0,
    total_records: 0,
  });
  const [tableEntries, setTableEntries] = useState<any[]>([]);
  const [checkAllIsChecked, setCheckAllIsChecked] = useState(false);
  const [checkedEntries, setCheckedEntries] = useState<number[]>([]);
  const [singleEntryToDelete, setSingleEntryToDelete] = useState<number>();
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [showInstructorSwitchModal, setShowInstructorSwitchModal] = useState<boolean>(false);
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState<boolean>(false);
  const [showMassDeleteWarningModal, setShowMassDeleteWarningModal] = useState<boolean>(false);
  const [memberIdToProcess, setMemberIdToProcess] = useState<number>();
  const [showMemberFormModal, setShowMemberFormModal] = useState<boolean>(false);

  useEffect(() => {
    getLkpInstructorList({ limit: 100, sort_by: "LAST_NAME" }).then((res) => {
      let instructorOpts: any[] = [{ isSelectAll: true, label: "All Instructors", value: "" }];
      if (res.success) {
        res.data?.forEach((e) =>
          instructorOpts.push({
            label: `${e.last_name} ${e.first_name}`,
            value: e.instructor_id,
            ...e,
          })
        );
      }
      setInstructorOptions(instructorOpts);
    });
  }, []);

  useEffect(() => {
    fetchData(params);
  }, [params]);

  useEffect(() => {
    setParams("page", 1);
    setParams("search", searchValue || "");
  }, [searchValue]);

  useEffect(() => {
    setParams("page", 1);
    setParams("search_by", searchBy);
  }, [searchBy]);

  useEffect(() => {
    setParams("my_members_only", tabValue === 1 ? false : true);
  }, [tabValue]);

  const fetchData = useCallback(
    async (parameters) => {
      if (loading) return;
      setLoading(true);
      let result = await getMemberList(parameters);
      setLoading(false);
      if (result.success) {
        setTableEntries(result.data);
        setPagination(result.pagination);
        setCheckAllIsChecked(false);
        setCheckedEntries([]);
      }
    },
    [setTableEntries, setPagination, loading, setLoading]
  );

  //#region event handlers
  const onUpdateStatus = (id, value) => {
    return new Promise((resolve) => {
      updateMemberStatus({ member_id: id, member_status: value })
        .then((result) => {
          if (result.success) {
            //show success message
            toast("Member state updated successfully", { type: "success" });
            setTableEntries((p) =>
              p.map((member) => {
                if (member.id == id) member.status = value;
                return member;
              })
            );
          } else {
            toast("Member state update failed", { type: "error" });
          }
        })
        .catch((e) => {
          toast("Member state update failed", { type: "error" });
        })
        .finally(() => resolve(true));
    });
  };

  const handleStatusChange = (values) => {
    setParams("status", values.join(","));
  };

  const handleInstructorFilterChange = (values) => {
    setParams("instructor_ids", values.join(","));
  };

  const handlePaginationOptionChange = (event: SelectChangeEvent<any>) => {
    let temp = { ...params, limit: event.target.value, page: 1 };
    setParams("limit", event.target.value);
    fetchData(temp);
  };

  const handlePageChange = (page: number) => {
    let temp = { ...params, page };
    setParams("page", page);
    fetchData(temp);
  };

  const handleSortChange = (sort_by: string, sort_order: string) => {
    let temp = { ...params, page: 1, sort_by, sort_order };
    setParams("page", 1);
    setParams("sort_by", sort_by);
    setParams("sort_order", sort_order);
    fetchData(temp);
  };

  const handleToggleCheckMember = (memberId: number) => {
    setCheckAllIsChecked(false);
    const i = checkedEntries.indexOf(memberId);
    if (i === -1) {
      setCheckedEntries([...checkedEntries, memberId]);
    } else {
      let entries = [...checkedEntries];
      entries.splice(i, 1);
      setCheckedEntries(entries);
    }
  };

  const handleToggleCheckAll = () => {
    if (checkAllIsChecked) {
      setCheckedEntries([]);
    } else {
      setCheckedEntries(tableEntries.map((m) => m.id));
    }
    setCheckAllIsChecked(!checkAllIsChecked);
  };

  const handleMassStatusSwitch = () => {
    if (checkedEntries.length === 0) {
      toast("You should first select members from the grid", { type: "error" });
      return;
    }
    setShowStatusUpdateModal(true);
  };

  const handleMassInstructorSwitch = () => {
    if (checkedEntries.length === 0) {
      toast("You should first select members from the grid", { type: "error" });
      return;
    }
    setShowInstructorSwitchModal(true);
  };

  const handleMassDelete = () => {
    if (checkedEntries.length === 0) {
      toast("You should first select members from the grid", { type: "error" });
      return;
    }
    setShowMassDeleteWarningModal(true);
  };

  const handleSignleEntryDelete = (v: number) => {
    setSingleEntryToDelete(v);
    setShowMassDeleteWarningModal(true);
  };

  const handleCloseSwitchModal = (isRefreshNeeded = false) => {
    setShowInstructorSwitchModal(false);
    setShowStatusUpdateModal(false);
    if (typeof isRefreshNeeded == "boolean" && isRefreshNeeded) {
      setLoading(true);
      window.location.reload();
    }
  };

  const handleUpdateInstructor = (memberId: number, instructorId: number) => {
    return new Promise((resolve) => {
      massReassignInstructor({
        instructor_id: instructorId,
        members: [memberId],
      })
        .then((result) => {
          if (result.success) {
            //show success message
            toast("Member instructor updated successfully", { type: "success" });
          } else {
            toast("Member instructor update failed", { type: "error" });
          }
        })
        .catch((e) => {
          toast("Member instructor update failed", { type: "error" });
        })
        .finally(() => resolve(true));
    });
  };

  const handleApplyMassDelete = (isConfirmed: boolean) => {
    if (isConfirmed) {
      let result = [];
      tableEntries?.forEach((member) => {
        if (checkedEntries.includes(member.id)) {
          if (MEMBER_STATUS_ALLOWED_FOR_DELETE.includes(member.status)) result.push(member.id);
        }
      });
      if (!result.length) {
        toast("None of the selected are deletable", { type: "info" });
        setShowMassDeleteWarningModal(false);
        return;
      }
      setLoading(true);
      let entriesToDelete = singleEntryToDelete ? [singleEntryToDelete] : result;
      setSingleEntryToDelete(undefined);
      massDelete({ members: entriesToDelete })
        .then((_res) => {
          window.location.reload();
        })
        .catch((e) => {
          console.log(e);
          toast(MSG_OPERATION_FAILED, { type: "error" });
        })
        .finally(() => setLoading(false));
    }
    setShowMassDeleteWarningModal(false);
  };

  const handleCloseMemberFormModal = (isRefreshNeeded = false) => {
    setShowMemberFormModal(false);
    if (typeof isRefreshNeeded == "boolean" && isRefreshNeeded) {
      setLoading(true);
      window.location.reload();
    }
  };

  const handleSearch = (searchValue) => {
    let value = searchValue;
    if (searchBy === "FITLINXX_ID" && value !== "") {
      value = parseInt(value);
    }
    setSearchValue(value);
  };
  //#endregion event handlers

  return (
    <>
      <div className={"members-main"}>
        <Tabs
          value={tabValue}
          onChange={setTabValue}
          tabs={[{ label: "My Members" }, { label: "All Members" }]}
          rightComp={
            <div className={"action-buttons"}>
              <CustomSelect
                multiple={true}
                options={instructorOptions}
                handler={handleInstructorFilterChange}
              />
              <CustomSelect defaultOptions={[DEFAULT_MEMBER_STATUS]} handler={handleStatusChange} />
              <Stack
                direction={"row"}
                alignItems="center"
                className="search-with-dropdown-container"
              >
                <SearchBar
                  className="search-container-with-dropdown"
                  placeholder="Search"
                  value={searchValue}
                  onSearch={handleSearch}
                  isAsync={true}
                />
                <Stack height={"90%"} style={{ background: "#d6eeec", width: 1 }} />
                <Dropdown
                  name="search_field"
                  label="All field"
                  className="base-dropdown"
                  value={searchBy}
                  defaultValue={""}
                  onChange={(e) => setSearchBy(`${e.target.value}`)}
                  showLabel={false}
                >
                  <MenuItem value="LAST_NAME">Last Name</MenuItem>
                  <MenuItem value="FIRST_NAME">First Name</MenuItem>
                  <MenuItem value="FITLINXX_ID">FitLinxx ID</MenuItem>
                </Dropdown>
              </Stack>
            </div>
          }
        />
        <ActionButtons
          onMassStatusSwitchClick={handleMassStatusSwitch}
          onMassInstructorSwitchClick={handleMassInstructorSwitch}
          onMassDeleteClick={handleMassDelete}
        />
        <TableContainer className="member-tasks-table" sx={{ paddingBottom: "20px" }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Box mt="30px">
                      <ScreenLoader isScreenLoader={true} />
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              <TableRow>
                <TableCell align="left" sx={{ padding: "0 0 0 20px", width: "38px" }}>
                  <Checkbox
                    checked={checkAllIsChecked || false}
                    // value={checkAllIsChecked || false}
                    icon={<CheckboxOff />}
                    checkedIcon={<CheckboxOn />}
                    onChange={handleToggleCheckAll}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                </TableCell>
                <TableCell sx={{ padding: 0, width: "43px" }}></TableCell>
                <TableCell align="left">
                  <SortButton
                    label={`Last Name`}
                    value={`LAST_NAME`}
                    sortingCallback={handleSortChange}
                    pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
                  />
                </TableCell>
                <TableCell align="left">
                  <SortButton
                    label={`First Name`}
                    value={`FIRST_NAME`}
                    sortingCallback={handleSortChange}
                    pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
                  />
                </TableCell>
                <TableCell align="left">
                  <SortButton
                    label={`FitLinxx ID`}
                    value={`FITLINXX_ID`}
                    sortingCallback={handleSortChange}
                    pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
                  />
                </TableCell>
                <TableCell align="left">
                  <SortButton
                    label={`Last Workout Session`}
                    value={`LAST_WORKOUT_SESSION`}
                    sortingCallback={handleSortChange}
                    pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
                  />
                </TableCell>
                <TableCell>Instructor</TableCell>
                <TableCell align="left">
                  <SortButton
                    label={`Member Status`}
                    value={`STATUS`}
                    sortingCallback={handleSortChange}
                    pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
                  />
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                tableEntries.map((member, index) => (
                  <TableItem
                    {...member}
                    instructorOptions={instructorOptions.filter((f) => !f.isSelectAll)}
                    key={`member_row__${member.id}_${index}`}
                    isChecked={checkedEntries.includes(member.id)}
                    onCheckMember={handleToggleCheckMember}
                    onUpdateStatus={onUpdateStatus}
                    onUpdateInstructor={handleUpdateInstructor}
                    onClickDelete={(v) => handleSignleEntryDelete(v)}
                    onClickEdit={(id) => {
                      console.log("onClickEdit", id);
                      setMemberIdToProcess(id);
                      setShowMemberFormModal(true);
                    }}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          {...pagination}
          onPageChange={handlePageChange}
          pageDropdownProps={{
            defaultValue: pagination.per_page,
            onChange: handlePaginationOptionChange,
            value: pagination.per_page,
          }}
        />
      </div>
      <MassInstructorSwitchModal
        open={showInstructorSwitchModal}
        onClose={handleCloseSwitchModal}
        memberIds={checkedEntries}
        instructorOptions={instructorOptions.filter((f) => !f.isSelectAll)}
        moduleName="member-task"
      />
      <MassStatusUpdateModal
        open={showStatusUpdateModal}
        onClose={handleCloseSwitchModal}
        memberIds={checkedEntries}
      />
      <MemberFormModal
        open={showMemberFormModal}
        onClose={handleCloseMemberFormModal}
        memberId={memberIdToProcess}
      />
      <ConfirmationBox
        open={showMassDeleteWarningModal}
        onClose={handleApplyMassDelete}
        mainTitle={"Confirm"}
        title={"Are you sure you want to delete the selected member(s)?"}
        message={<>Note that only members with status trial or demo will be deleted.</>}
        type={"confirmation"}
      />
    </>
  );
}

export default MemberTasks;
